import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useOffice } from '@/hooks/useOffice';
import { useAuth } from '@/hooks/useAuth';
import TapeEditModal from '@/pages/tapes/components/TapeEditModal';
import CreatableSelect from '@/components/CreatableSelect';
import { logActivity } from '@/lib/activityLog';

interface Tape {
  id: number;
  media_label: string;
  media_set: string | null;
  modified_date: string | null;
  tape_type: string | null;
  backup_set: string | null;
  location: string | null;
  box_number: string | null;
  notes: string | null;
  needs_replacement: boolean;
}

interface TapeRequest {
  id: number;
  office_id: number;
  requested_by: string | null;
  requested_by_name: string | null;
  quantity: number;
  notes: string | null;
  status: string;
  created_at: string;
}

export default function ReplacementPage() {
  const { currentOfficeId } = useOffice();
  const { user, profile } = useAuth();
  const [tapes, setTapes] = useState<Tape[]>([]);
  const [requests, setRequests] = useState<TapeRequest[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [replacing, setReplacing] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [undoOpen, setUndoOpen] = useState(false);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [singleUndo, setSingleUndo] = useState<Tape | null>(null);

  // Batch set tape type modal state
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [newType, setNewType] = useState('');
  const [typeUpdating, setTypeUpdating] = useState(false);

  // Request-new-tapes modal state
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestQty, setRequestQty] = useState(1);
  const [requestNotes, setRequestNotes] = useState('');
  const [requesting, setRequesting] = useState(false);

  // New-tape modal state (reuses the Tapes page create modal)
  const [showNew, setShowNew] = useState(false);
  const [boxNumbers, setBoxNumbers] = useState<string[]>([]);
  const [masterOptions, setMasterOptions] = useState<Record<string, string[]>>({
    media_set: [],
    tape_type: [],
    location: [],
    backup_set: [],
  });
  const [allTapeOptionsRaw, setAllTapeOptionsRaw] = useState<{
    types: string[];
    locations: string[];
    sets: string[];
    backupSets: string[];
  }>({ types: [], locations: [], sets: [], backupSets: [] });

  // Merge actual values from the loaded tapes with the persisted master options so
  // the dropdowns in modals (New Tape / Set Tape Type) show all types that exist
  // in the database, not just the hard-coded defaults or empty master options.
  const derivedOptions = useMemo(() => ({
    types: Array.from(new Set([
      ...allTapeOptionsRaw.types,
      ...masterOptions.tape_type,
    ])),
    locations: Array.from(new Set([
      ...allTapeOptionsRaw.locations,
      ...masterOptions.location,
    ])),
    sets: Array.from(new Set([
      ...allTapeOptionsRaw.sets,
      ...masterOptions.media_set,
    ])),
    backupSets: Array.from(new Set([
      ...allTapeOptionsRaw.backupSets,
      ...masterOptions.backup_set,
    ])),
  }), [allTapeOptionsRaw, masterOptions]);

  const load = async () => {
    if (!currentOfficeId) return;
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('tapes')
        .select('*')
        .eq('office_id', currentOfficeId)
        .eq('needs_replacement', true)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      setTapes(data as Tape[]);
      setSelected(new Set());
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    if (!currentOfficeId) return;
    try {
      const { data, error } = await supabase
        .from('tape_requests')
        .select('*')
        .eq('office_id', currentOfficeId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setRequests(data as TapeRequest[]);
    } catch (e: any) {
      console.error('Failed to load tape requests', e);
    }
  };

  useEffect(() => {
    load();
    loadRequests();
  }, [currentOfficeId]);

  useEffect(() => {
    if (!currentOfficeId) return;
    const loadBoxes = async () => {
      const { data, error } = await supabase
        .from('boxes')
        .select('box_number')
        .eq('office_id', currentOfficeId)
        .order('box_number');
      if (error) return;
      setBoxNumbers(
        Array.from(new Set((data as { box_number: string }[]).map((b) => b.box_number).filter(Boolean))),
      );
    };
    loadBoxes();
  }, [currentOfficeId]);

  useEffect(() => {
    if (!currentOfficeId) return;
    const loadOptions = async () => {
      const { data, error } = await supabase
        .from('tape_options')
        .select('category, value')
        .eq('office_id', currentOfficeId)
        .order('created_at');
      if (error) return;
      const grouped: Record<string, string[]> = { media_set: [], tape_type: [], location: [], backup_set: [] };
      (data as { category: string; value: string }[]).forEach((r) => {
        if (grouped[r.category] && r.value && !grouped[r.category].includes(r.value)) {
          grouped[r.category].push(r.value);
        }
      });
      setMasterOptions(grouped);
    };
    loadOptions();
  }, [currentOfficeId]);

  useEffect(() => {
    if (!currentOfficeId) return;
    const loadAllOptions = async () => {
      const { data, error } = await supabase
        .from('tapes')
        .select('tape_type, media_set, backup_set, location')
        .eq('office_id', currentOfficeId);
      if (error) return;
      const rows = (data as Partial<Tape>[]) ?? [];
      setAllTapeOptionsRaw({
        types: Array.from(new Set(rows.map((t) => t.tape_type).filter(Boolean))) as string[],
        locations: Array.from(new Set(rows.map((t) => t.location).filter(Boolean))) as string[],
        sets: Array.from(new Set(rows.map((t) => t.media_set).filter(Boolean))) as string[],
        backupSets: Array.from(new Set(rows.map((t) => t.backup_set).filter(Boolean))) as string[],
      });
    };
    loadAllOptions();
  }, [currentOfficeId]);

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allSelected = useMemo(
    () => tapes.length > 0 && tapes.every((t) => selected.has(t.id)),
    [tapes, selected],
  );

  const sortedTapes = useMemo(() => {
    const sorted = [...tapes].sort((a, b) => {
      const la = (a.media_label || '').toLowerCase();
      const lb = (b.media_label || '').toLowerCase();
      if (la < lb) return sortDir === 'asc' ? -1 : 1;
      if (la > lb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [tapes, sortDir]);

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(tapes.map((t) => t.id)));
  };

  const markReplaced = async (ids: number[]) => {
    setReplacing(true);
    setError('');
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;
    try {
      const { error } = await supabase
        .from('tapes')
        .update({
          needs_replacement: false,
          box_number: 'Backup Tape Library',
          modified_date: todayStr,
          updated_at: new Date().toISOString(),
        })
        .in('id', ids);
      if (error) throw error;

      setTapes((s) => s.filter((t) => !ids.includes(t.id)));
      setSelected(new Set());
      setConfirmOpen(false);
      await logActivity('replace_tape', `Marked ${ids.length} tape(s) as replaced`);
    } catch (e: any) {
      setError(e?.message || 'Failed to mark tapes as replaced');
    } finally {
      setReplacing(false);
    }
  };

  const undoReplacement = async (ids: number[]) => {
    setUndoing(true);
    setError('');
    try {
      const { error } = await supabase
        .from('tapes')
        .update({
          needs_replacement: false,
          updated_at: new Date().toISOString(),
        })
        .in('id', ids);
      if (error) throw error;

      setTapes((s) => s.filter((t) => !ids.includes(t.id)));
      setSelected(new Set());
      setUndoOpen(false);
      await logActivity('undo_replacement', `Undid replacement for ${ids.length} tape(s)`);
    } catch (e: any) {
      setError(e?.message || 'Failed to undo replacement');
    } finally {
      setUndoing(false);
    }
  };

  const updateTapeType = async (ids: number[]) => {
    const type = newType.trim();
    if (!type) return;
    setTypeUpdating(true);
    setError('');
    try {
      const { error } = await supabase
        .from('tapes')
        .update({ tape_type: type, updated_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;

      setTapes((s) => s.map((t) => (ids.includes(t.id) ? { ...t, tape_type: type } : t)));
      setSelected(new Set());
      setTypeModalOpen(false);
      setNewType('');
      await logActivity('update_tape_type', `Set tape type to "${type}" for ${ids.length} tape(s)`);
    } catch (e: any) {
      setError(e?.message || 'Failed to update tape type');
    } finally {
      setTypeUpdating(false);
    }
  };

  const submitRequest = async () => {
    if (!currentOfficeId) return;
    setRequesting(true);
    try {
      const qty = Math.max(1, Math.floor(requestQty) || 1);
      const { error } = await supabase.from('tape_requests').insert({
        office_id: currentOfficeId,
        requested_by: user?.id ?? null,
        requested_by_name: profile?.username ?? user?.email ?? 'Unknown',
        quantity: qty,
        notes: requestNotes.trim() || null,
        status: 'open',
      });
      if (error) throw error;
      setRequestOpen(false);
      setRequestQty(1);
      setRequestNotes('');
      loadRequests();
      await logActivity('request_tapes', `Requested ${qty} new tape(s)`);
    } catch (e: any) {
      setError(e?.message || 'Failed to submit request');
    } finally {
      setRequesting(false);
    }
  };

  const fulfillRequest = async (id: number) => {
    try {
      const { error } = await supabase
        .from('tape_requests')
        .update({ status: 'fulfilled' })
        .eq('id', id);
      if (error) throw error;
      loadRequests();
      await logActivity('fulfill_request', `Fulfilled tape request #${id}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to update request');
    }
  };

  const cancelRequest = async (id: number) => {
    try {
      const { error } = await supabase
        .from('tape_requests')
        .update({ status: 'cancelled' })
        .eq('id', id);
      if (error) throw error;
      loadRequests();
      await logActivity('cancel_request', `Cancelled tape request #${id}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to cancel request');
    }
  };

  const openRequests = useMemo(() => requests.filter((r) => r.status === 'open'), [requests]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Tape Replacement</h1>
          <p className="text-sm text-slate-500 mt-1">
            {tapes.length} tapes marked as needing replacement
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {selected.size > 0 && (
            <>
              <button
                onClick={() => setTypeModalOpen(true)}
                disabled={replacing || undoing}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center" />
                Set Tape Type ({selected.size})
              </button>
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={replacing || undoing}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-check-line w-4 h-4 flex items-center justify-center" />
                {replacing ? 'Replacing...' : `Mark Replaced (${selected.size})`}
              </button>
              <button
                onClick={() => setUndoOpen(true)}
                disabled={replacing || undoing}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-arrow-go-back-line w-4 h-4 flex items-center justify-center" />
                {undoing ? 'Undoing...' : `Undo (${selected.size})`}
              </button>
            </>
          )}
          <button
            onClick={() => setRequestOpen(true)}
            className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 cursor-pointer"
          >
            <i className="ri-add-circle-line w-4 h-4 flex items-center justify-center" />
            Request New Tapes
          </button>
        </div>
      </div>

      {openRequests.length > 0 && (
        <div className="mb-4 space-y-2">
          {openRequests.map((r) => (
            <div
              key={r.id}
              className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-center justify-between gap-3"
            >
              <div className="flex items-start gap-3">
                <i className="ri-information-line w-5 h-5 flex items-center justify-center shrink-0 mt-0.5" />
                <div>
                  <p>
                    <strong>{r.requested_by_name || 'A user'}</strong> requested{' '}
                    <strong>{r.quantity}</strong> new tape{r.quantity > 1 ? 's' : ''} to be added.
                    {r.notes ? <span className="text-amber-700"> Note: {r.notes}</span> : null}
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">Requested on {formatDate(r.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setShowNew(true)}
                  className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 cursor-pointer"
                >
                  <i className="ri-add-line w-4 h-4 flex items-center justify-center" />
                  Create Tape
                </button>
                <button
                  onClick={() => fulfillRequest(r.id)}
                  className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-amber-300 text-amber-800 text-xs font-medium hover:bg-amber-100 cursor-pointer"
                >
                  <i className="ri-check-line w-4 h-4 flex items-center justify-center" />
                  Mark Done
                </button>
                <button
                  onClick={() => cancelRequest(r.id)}
                  className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 text-xs font-medium hover:bg-slate-100 cursor-pointer"
                >
                  <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-4 p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-3">
        <i className="ri-alert-line w-5 h-5 flex items-center justify-center shrink-0 mt-0.5" />
        <span>
          These tapes are flagged for physical replacement. Check the box next to a tape — or use
          the <strong>select-all</strong> checkbox in the header — then click{' '}
          <strong>Mark Replaced</strong> once they have been swapped out.
        </span>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer whitespace-nowrap">
            Retry
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_theme(colors.slate.200)]">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium rounded-tl-lg">
                <button
                  onClick={toggleSort}
                  className="inline-flex items-center gap-1 hover:text-slate-700 cursor-pointer whitespace-nowrap"
                  title="Sort by Media Label"
                >
                  Media Label
                  <i
                    className={
                      sortDir === 'asc'
                        ? 'ri-arrow-up-line w-4 h-4 flex items-center justify-center'
                        : 'ri-arrow-down-line w-4 h-4 flex items-center justify-center'
                    }
                  />
                </button>
              </th>
              <th className="text-left px-4 py-2.5 font-medium">Media Set</th>
              <th className="text-left px-4 py-2.5 font-medium">Tape Type</th>
              <th className="text-left px-4 py-2.5 font-medium">Location</th>
              <th className="text-left px-4 py-2.5 font-medium">Box Number</th>
              <th className="text-left px-4 py-2.5 font-medium">Modified</th>
              <th className="text-center px-4 py-2.5 font-medium">
                <div className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={tapes.length === 0}
                    className="w-4 h-4 cursor-pointer accent-emerald-600 disabled:cursor-not-allowed"
                    title="Select all tapes on this page"
                  />
                  <span className="whitespace-nowrap">Replaced</span>
                </div>
              </th>
              <th className="text-center px-4 py-2.5 font-medium rounded-tr-lg">
                <span className="whitespace-nowrap">Undo</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-slate-400 text-sm">Loading...</td>
              </tr>
            )}
            {!loading &&
              sortedTapes.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium font-mono">{t.media_label}</td>
                  <td className="px-4 py-2.5 text-slate-600">{t.media_set || '—'}</td>
                  <td className="px-4 py-2.5">
                    {t.tape_type ? (
                      <span className="inline-block px-2 py-0.5 text-xs rounded bg-rose-50 text-rose-700">
                        {t.tape_type}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{t.location || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{t.box_number || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{t.modified_date || '—'}</td>
                  <td className="px-4 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggleOne(t.id)}
                      className="w-4 h-4 cursor-pointer accent-emerald-600"
                      title="Select this tape for replacement"
                    />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <button
                      onClick={() => setSingleUndo(t)}
                      disabled={undoing}
                      title="Undo replacement flag"
                      className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-go-back-line w-4 h-4 flex items-center justify-center" />
                    </button>
                  </td>
                </tr>
              ))}
            {!loading && tapes.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-slate-400 text-sm">
                  No tapes need replacement
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {typeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !typeUpdating && setTypeModalOpen(false)}
          />
          <div className="relative w-full max-w-md bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold">Set Tape Type</h3>
            <p className="mt-1 text-sm text-slate-600">
              Update the tape type for <strong className="text-slate-900">{selected.size}</strong>{' '}
              selected tape{selected.size > 1 ? 's' : ''}.
            </p>
            <div className="mt-5">
              <label className="text-xs text-slate-500 mb-1 block">Tape Type</label>
              <CreatableSelect
                value={newType}
                onChange={setNewType}
                options={derivedOptions.types}
                placeholder="Select or type a tape type..."
                clearable
              />
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setTypeModalOpen(false)}
                disabled={typeUpdating}
                className="whitespace-nowrap px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => updateTapeType(Array.from(selected))}
                disabled={typeUpdating || !newType.trim()}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-check-line w-4 h-4 flex items-center justify-center" />
                {typeUpdating ? 'Updating...' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !replacing && setConfirmOpen(false)}
          />
          <div className="relative w-full max-w-md bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold">Mark Tapes as Replaced</h3>
            <p className="mt-2 text-sm text-slate-600">
              You are about to mark <strong className="text-slate-900">{selected.size}</strong> tape
              {selected.size > 1 ? 's' : ''} as replaced. They will be moved back into the Tape
              Library and their modified date will be set to today. This action cannot be undone.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={replacing}
                className="whitespace-nowrap px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => markReplaced(Array.from(selected))}
                disabled={replacing}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-check-line w-4 h-4 flex items-center justify-center" />
                {replacing ? 'Replacing...' : 'Replace'}
              </button>
            </div>
          </div>
        </div>
      )}

      {undoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !undoing && setUndoOpen(false)}
          />
          <div className="relative w-full max-w-md bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold">Undo Replacement</h3>
            <p className="mt-2 text-sm text-slate-600">
              You are about to remove the <strong className="text-slate-900">needs replacement</strong>{' '}
              flag from <strong className="text-slate-900">{selected.size}</strong> tape
              {selected.size > 1 ? 's' : ''}. They will return to the normal tape list and keep their
              original box number and modified date.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setUndoOpen(false)}
                disabled={undoing}
                className="whitespace-nowrap px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => undoReplacement(Array.from(selected))}
                disabled={undoing}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-arrow-go-back-line w-4 h-4 flex items-center justify-center" />
                {undoing ? 'Undoing...' : 'Undo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {singleUndo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !undoing && setSingleUndo(null)}
          />
          <div className="relative w-full max-w-md bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold">Undo Replacement</h3>
            <p className="mt-2 text-sm text-slate-600">
              You are about to remove the <strong className="text-slate-900">needs replacement</strong>{' '}
              flag from <strong className="text-slate-900">{singleUndo.media_label}</strong>. It will
              return to the normal tape list and keep its original box number and modified date.
            </p>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setSingleUndo(null)}
                disabled={undoing}
                className="whitespace-nowrap px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  undoReplacement([singleUndo.id]);
                  setSingleUndo(null);
                }}
                disabled={undoing}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-arrow-go-back-line w-4 h-4 flex items-center justify-center" />
                {undoing ? 'Undoing...' : 'Undo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {requestOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !requesting && setRequestOpen(false)}
          />
          <div className="relative w-full max-w-md bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="text-lg font-semibold">Request New Tapes</h3>
            <p className="mt-1 text-sm text-slate-600">
              Let other users know how many new tapes need to be added.
            </p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Number of new tapes</label>
                <input
                  type="number"
                  min={1}
                  value={requestQty}
                  onChange={(e) => setRequestQty(Math.max(1, Math.floor(Number(e.target.value)) || 1))}
                  className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Note (optional)</label>
                <textarea
                  value={requestNotes}
                  onChange={(e) => setRequestNotes(e.target.value)}
                  rows={3}
                  placeholder="e.g. New daily backup tapes for Q4"
                  className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 resize-none"
                />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setRequestOpen(false)}
                disabled={requesting}
                className="whitespace-nowrap px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={submitRequest}
                disabled={requesting}
                className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 disabled:opacity-50 cursor-pointer"
              >
                <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center" />
                {requesting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <TapeEditModal
          tape={null}
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            load();
            loadRequests();
          }}
          allMediaSets={derivedOptions.sets}
          allBackupSets={derivedOptions.backupSets}
          allTapeTypes={derivedOptions.types}
          allLocations={derivedOptions.locations}
          allBoxNumbers={boxNumbers}
          defaultNeedsReplacement
        />
      )}
    </div>
  );
}
